import React, { useState } from "react";
import Button from "react-bootstrap/Button";
import { ProductListInterfaceForWriteOffPage, ReactSelectInterface } from "../../../../../interfaces/interfaces";
import { ProductWriteOffInput } from "@me-team/host/main/graphql/types";
import { useTranslation } from "react-i18next";
import WriteOffService from "../../../../../services/WriteOffService/WriteOffService";
import { ReactSelect, TooltipCustom } from "@services/ui-components";

type OtherWriteOffProductsTableRowProps = {
    product: ProductListInterfaceForWriteOffPage;
    changeProductsToWriteOff: (
        productToWriteOff: ProductWriteOffInput,
        changedField: keyof ProductWriteOffInput
    ) => void;
    removeProductFromWriteOff: (productId: number, isProductFromTechMap: boolean) => void;
};
const OtherWriteOffProductsTableRow: React.FC<OtherWriteOffProductsTableRowProps> = ({
    product,
    changeProductsToWriteOff,
    removeProductFromWriteOff,
}) => {
    const { t: translate } = useTranslation();
    const [selectedStorage, setSelectedStorage] = useState<ReactSelectInterface>(
        product.storage ? { value: product.storage.id, label: product.storage.name } : null
    );
    const [selectedUnit, setSelectedUnit] = useState<ReactSelectInterface>({
        label: product.unit.name,
        value: product.unit.id,
    });
    const [selectedQty, setSelectedQty] = useState<string>(product.qty === null ? "" : String(product.qty));
    const [writeOffProductInfo, setWriteOffProductInfo] = useState<ProductWriteOffInput>({
        product: product.id,
        storage: product.storage ? product.storage.id : null,
        quantity: product.qty,
        unit: product.unit.id,
        isZeroAccept: product.isZeroAccept,
    });

    const onSelectStorage = (storage: ReactSelectInterface) => {
        setSelectedStorage(storage);
        setWriteOffProductInfo((prevState) => {
            return {
                ...prevState,
                storage: storage.value,
            };
        });
        changeProductsToWriteOff({ ...writeOffProductInfo, storage: storage.value }, "storage");
    };

    const onChangeQtyProductHandler = (event: React.ChangeEvent<HTMLInputElement>) => {
        const value = event.target.value;

        if (value.length > 1 && value.startsWith("0")) return;

        if (value.length > 9) return;

        if (!/^\d*$/.test(value)) return;

        const currentQty = value ? Number(value) : null;
        setSelectedQty(value);
        setWriteOffProductInfo((prevState) => ({
            ...prevState,
            quantity: currentQty,
        }));
        changeProductsToWriteOff({ ...writeOffProductInfo, quantity: currentQty }, "quantity");
    };

    const onSelectUnit = (unit: ReactSelectInterface) => {
        setSelectedUnit(unit);
        setWriteOffProductInfo((prevState) => {
            return {
                ...prevState,
                unit: unit.value,
            };
        });
        changeProductsToWriteOff({ ...writeOffProductInfo, unit: unit.value }, "unit");
    };

    const onRemoveProduct = () => {
        removeProductFromWriteOff(product.id, false);
    };

    return (
        <tr key={product.id}>
            <td className="col-2 ps-4 py-4">{product.name}</td>
            <td className="col-1 py-4"></td>
            <td className="col-3 py-4">
                <ReactSelect
                    id="write-off-product-storage-field"
                    value={selectedStorage}
                    options={WriteOffService.reactSelectStorage(product.storages)}
                    placeholder={translate("Warehouse")}
                    onChange={onSelectStorage}
                    disabled={product.isZeroAccept}
                    isSearchable={false}
                    hasError={!!product.errors.storageError}
                />
                {product.errors.storageError && (
                    <span className={"text-danger fs-7"}>{product.errors.storageError}</span>
                )}
            </td>
            <td className="col-2 py-4">
                <input
                    id="writeOff-produt-qty-field"
                    type="text"
                    inputMode="numeric"
                    className={`form-control pe-4 ${product.errors.qtyError ? "is-invalid" : ""}`}
                    placeholder={translate("quantity")}
                    value={selectedQty}
                    onChange={onChangeQtyProductHandler}
                    onKeyDown={(e) => {
                        if (e.key === "-" || e.key === "e") {
                            e.preventDefault();
                        }
                    }}
                />
                {product.errors.qtyError && <span className={"text-danger fs-7"}>{product.errors.qtyError}</span>}
            </td>
            <td className="col-2 py-4">
                <ReactSelect
                    id="write-off-product-unit-field"
                    value={selectedUnit}
                    options={WriteOffService.reactSelectUnits(product.units)}
                    placeholder={""}
                    onChange={onSelectUnit}
                    isSearchable={false}
                />
            </td>
            <td className="col-1 py-4"></td>
            <td className="col-1 text-end pe-0 py-4">
                {!product.isZeroAccept && (
                    <TooltipCustom placement="bottom" text={translate("Delete Product")}>
                        <Button className={"btn rounded border text-dark bg-white rounded-1"} onClick={onRemoveProduct}>
                            <i className="bi bi-trash3 d-flex fs-normal"></i>
                        </Button>
                    </TooltipCustom>
                )}
                {product.isZeroAccept && (
                    <TooltipCustom
                        placement="bottom"
                        align={"center"}
                        text={translate("To cancel the item write‑off, set its quantity to 0")}
                    >
                        <Button variant={"secondary"} className={"border rounded-1"}>
                            <i className="bi bi-trash3 d-flex text-secondary fs-normal"></i>
                        </Button>
                    </TooltipCustom>
                )}
            </td>
        </tr>
    );
};

export default React.memo(OtherWriteOffProductsTableRow);
